/**
 * DashboardPage - Main hub showing active features
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Brain,
  Wallet,
  Heart,
  Dumbbell,
  ChevronRight,
  Settings,
  Lock,
  LogOut,
  Bell,
  UserCheck,
  X,
  Users,
} from 'lucide-react';
import { useMapStore } from '../stores/mapStore';
import { useFeatureAccessStore, FEATURES, FEATURE_INFO } from '../stores/featureAccessStore';
import { useCoachStore } from '../stores/coachStore';

// Feature icon mapping
const FEATURE_ICONS = {
  mindmap: Brain,
  cashflow: Wallet,
  moodmap: Heart,
  fittrack: Dumbbell,
};

// Feature color mapping
const FEATURE_COLORS = {
  mindmap: {
    bg: 'from-indigo-600/20 to-purple-600/20',
    border: 'border-indigo-500/30',
    icon: 'bg-indigo-500/20 text-indigo-400',
    button: 'bg-indigo-600 hover:bg-indigo-500',
  },
  cashflow: {
    bg: 'from-emerald-600/20 to-teal-600/20',
    border: 'border-emerald-500/30',
    icon: 'bg-emerald-500/20 text-emerald-400',
    button: 'bg-emerald-600 hover:bg-emerald-500',
  },
  moodmap: {
    bg: 'from-purple-600/20 to-pink-600/20',
    border: 'border-purple-500/30',
    icon: 'bg-purple-500/20 text-purple-400',
    button: 'bg-purple-600 hover:bg-purple-500',
  },
  fittrack: {
    bg: 'from-orange-600/20 to-amber-600/20',
    border: 'border-orange-500/30',
    icon: 'bg-orange-500/20 text-orange-400',
    button: 'bg-orange-600 hover:bg-orange-500',
  },
};

const FeatureCard = ({ feature, isEnabled, onClick }) => {
  const Icon = FEATURE_ICONS[feature.feature_id] || Brain;
  const colors = FEATURE_COLORS[feature.feature_id] || FEATURE_COLORS.mindmap;

  if (!isEnabled) {
    return (
      <div className="relative p-6 bg-gray-900/50 border border-gray-800 rounded-xl opacity-60">
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-12 h-12 ${colors.icon} rounded-xl flex items-center justify-center opacity-50`}>
            <Icon size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-400">{feature.name}</h3>
              <Lock size={14} className="text-gray-500" />
            </div>
            <p className="text-sm text-gray-600">{feature.description}</p>
          </div>
        </div>
        <div className="text-center py-2 text-sm text-gray-500">
          This feature is not enabled for your account
        </div>
      </div>
    );
  }

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className={`relative p-6 bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-xl cursor-pointer group`}
    >
      <div className="flex items-center gap-4 mb-4">
        <div className={`w-12 h-12 ${colors.icon} rounded-xl flex items-center justify-center`}>
          <Icon size={24} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white">{feature.name}</h3>
          <p className="text-sm text-gray-400">{feature.description}</p>
        </div>
        <ChevronRight size={20} className="text-gray-400 group-hover:text-white transition-colors" />
      </div>

      <div className="absolute inset-0 border-2 border-white/0 group-hover:border-white/10 rounded-xl transition-colors" />
    </motion.div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, profile, signOut } = useMapStore();
  const { allFeatures, enabledFeatures, isLoading, isAdmin, initialize } = useFeatureAccessStore();
  const {
    pendingInvitations,
    myCoaches,
    loadPendingInvitations,
    loadMyCoaches,
    isLoadingInvitations,
    checkCoachStatus,
    isActingAsCoach,
    hasClients,
  } = useCoachStore();
  const [dismissedInvitations, setDismissedInvitations] = useState(false);
  const [dismissedPendingCoaches, setDismissedPendingCoaches] = useState(false);

  useEffect(() => {
    if (user?.id) {
      initialize(user.id);
      checkCoachStatus();
      loadPendingInvitations();
      loadMyCoaches();
    }
  }, [user?.id, initialize, checkCoachStatus, loadPendingInvitations, loadMyCoaches]);

  // Filter pending coach invitations (invitations sent by me)
  const pendingCoachInvitations = myCoaches.filter(coach => coach.status === 'pending');

  // Separate enabled and disabled features
  const sortedFeatures = [...allFeatures].sort((a, b) => {
    const aEnabled = enabledFeatures.includes(a.feature_id);
    const bEnabled = enabledFeatures.includes(b.feature_id);
    if (aEnabled && !bEnabled) return -1;
    if (!aEnabled && bEnabled) return 1;
    return 0;
  });

  const displayName = profile?.display_name || user?.email?.split('@')[0] || 'User';

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      {/* Minimal Header */}
      <div className="max-w-4xl mx-auto mb-4">
        <div className="flex items-center justify-end gap-2">
          {/* Coach Panel */}
          {(isActingAsCoach || hasClients) && (
            <button
              onClick={() => navigate('/coach')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 rounded-lg transition-colors text-sm"
              title="Koç Paneli"
            >
              <Users size={16} />
              <span className="hidden xs:inline">Koç</span>
            </button>
          )}
          {/* My Coaches */}
          {myCoaches.length > 0 && (
            <button
              onClick={() => navigate('/coaches')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 rounded-lg transition-colors text-sm"
              title="Koçlarım"
            >
              <UserCheck size={16} />
              <span className="hidden xs:inline">Koçlarım</span>
            </button>
          )}
          {/* Admin */}
          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded-lg transition-colors"
              title="Admin Paneli"
            >
              <Settings size={16} />
            </button>
          )}
          {/* Profile */}
          <button
            onClick={() => navigate('/profile')}
            className="w-8 h-8 rounded-full overflow-hidden"
            title="Profil"
          >
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </button>
          {/* Logout */}
          <button
            onClick={signOut}
            className="p-2 bg-gray-800 hover:bg-red-600/30 text-gray-400 hover:text-red-400 rounded-lg transition-colors"
            title="Çıkış Yap"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>

      {/* Pending Coach Invitations (I invited someone as coach) */}
      {pendingCoachInvitations.length > 0 && !dismissedPendingCoaches && (
        <div className="max-w-4xl mx-auto mb-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-white">
                    {pendingCoachInvitations.length} bekleyen koç davetiyeleri
                  </p>
                  <p className="text-sm text-blue-300/80">
                    Gönderdiğiniz davetiyeler henüz yanıtlanmadı
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/coaches')}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <UserCheck className="w-4 h-4" />
                  Görüntüle
                </button>
                <button
                  onClick={() => setDismissedPendingCoaches(true)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                  title="Kapat"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Coach Invitation Banner (Someone invited me as coach) */}
      {pendingInvitations.length > 0 && !dismissedInvitations && (
        <div className="max-w-4xl mx-auto mb-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-orange-600/20 to-amber-600/20 border border-orange-500/30 rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="font-medium text-white">
                    {pendingInvitations.length} yeni koçluk daveti
                  </p>
                  <p className="text-sm text-orange-300/80">
                    Birisi sizi koç olarak davet etti
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/coach')}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <UserCheck className="w-4 h-4" />
                  Görüntüle
                </button>
                <button
                  onClick={() => setDismissedInvitations(true)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                  title="Kapat"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Features Grid */}
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sortedFeatures.map((feature) => (
            <FeatureCard
              key={feature.feature_id}
              feature={feature}
              isEnabled={enabledFeatures.includes(feature.feature_id)}
              onClick={() => navigate(feature.route)}
            />
          ))}
        </div>

        {/* Empty state if no features enabled */}
        {enabledFeatures.length === 0 && (
          <div className="text-center py-16 bg-gray-900/50 rounded-2xl border border-gray-800 mt-8">
            <Lock size={48} className="mx-auto text-gray-700 mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No features enabled
            </h3>
            <p className="text-gray-600">
              Contact an administrator to enable features for your account
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default DashboardPage;
